import React, { useState, useMemo, useRef, useEffect } from 'react';
import moment from 'moment';
import { useAppDispatch, useAppSelector } from 'src/store';
import { NewProjectDefinitionFormState, setFormState } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSlice';
import { getCurrentProjectDefinitionFormState } from 'src/app/projects/views/new-project-definition/state/newProjectDefinitionSelector';
import { PrimaryButton, SecondaryButton } from 'src/shared/components/button/Button';
import Input from 'src/shared/components/input/Input';
import BasicDatePicker from 'src/shared/components/basic-datepicker/BasicDatePicker';
import QuestionButtonHelp from 'src/shared/components/question-button-help/QuestionButtonHelp';
import classNames from 'classnames';
import { Plus, Copy, Trash2, ChevronUp, ChevronDown, AlertCircle } from 'lucide-react';
import commonStyles from 'src/app/projects/views/new-project-definition/NewProjectDefinition.module.scss';
import styles from './SamplingEventsManager.module.scss';
import { validateSamplingEvent } from '../samplingCadence.utils';

type SamplingEventWithId = {
    name: string;
    fromDate: string;
    toDate: string;
    year: number;
    id: string;
};

const SamplingEventsManager = () => {
    const dispatch = useAppDispatch();
    const formState = useAppSelector(getCurrentProjectDefinitionFormState);
    const { samplingEvents } = formState;
    const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
    const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());
    const eventRefs = useRef<Record<string, HTMLDivElement | null>>({});
    const [focusedEventId, setFocusedEventId] = useState<string | null>(null);
    // Maintain stable IDs array that corresponds to samplingEvents
    // IDs are only added/removed/reordered when the array length or order changes
    const eventIdsRef = useRef<string[]>([]);
    const nextIdRef = useRef<number>(0);

    // Convert events to include unique IDs and infer year from fromDate
    const eventsWithIds: SamplingEventWithId[] = useMemo(() => {
        const currentLength = samplingEvents.length;
        const idsLength = eventIdsRef.current.length;

        // If array length changed, update IDs array
        if (currentLength !== idsLength) {
            if (currentLength > idsLength) {
                // Events were added - add new IDs
                const newIds = Array.from({ length: currentLength - idsLength }, () => `event-${nextIdRef.current++}`);
                eventIdsRef.current = [...eventIdsRef.current, ...newIds];
            } else {
                // Events were removed - remove corresponding IDs
                eventIdsRef.current = eventIdsRef.current.slice(0, currentLength);
            }
        }

        return samplingEvents.map((event, index) => {
            // Always derive year from fromDate if available, otherwise use stored year or current year
            let year: number;
            if (event.fromDate && event.fromDate.trim() !== '') {
                const parsedYear = moment(event.fromDate).year();
                year = isNaN(parsedYear) || parsedYear <= 0 ? (event.year && event.year > 0 ? event.year : moment().year()) : parsedYear;
            } else {
                // If no fromDate, use stored year if valid, otherwise current year
                year = event.year && event.year > 0 ? event.year : moment().year();
            }
            return {
                name: event.name || '',
                fromDate: event.fromDate || '',
                toDate: event.toDate || '',
                year: year,
                id: eventIdsRef.current[index] || `event-${nextIdRef.current++}`,
            };
        });
    }, [samplingEvents]);

    // Group events by year
    const eventsByYear = useMemo(() => {
        const grouped: Record<number, SamplingEventWithId[]> = {};
        eventsWithIds.forEach(event => {
            const year = event.year;
            if (!grouped[year]) {
                grouped[year] = [];
            }
            grouped[year].push(event);
        });
        // Sort years in descending order and events within each year
        return Object.keys(grouped)
            .map(Number)
            .sort((a, b) => b - a)
            .reduce((acc, year) => {
                acc[year] = grouped[year].sort((a, b) => {
                    const dateA = moment(a.fromDate);
                    const dateB = moment(b.fromDate);
                    return dateA.isBefore(dateB) ? -1 : 1;
                });
                return acc;
            }, {} as Record<number, SamplingEventWithId[]>);
    }, [eventsWithIds]);

    const onFormChange = (fragment: Partial<NewProjectDefinitionFormState>) => {
        dispatch(setFormState(fragment));
    };

    const updateEvents = (updatedEvents: SamplingEventWithId[]) => {
        const eventsWithoutIds = updatedEvents.map(({ id: _id, ...event }) => event);
        onFormChange({ samplingEvents: eventsWithoutIds });
    };

    const addEvent = (year?: number) => {
        const currentYear = year || moment().year();
        const newEvent: SamplingEventWithId = {
            id: `event-${nextIdRef.current++}`,
            name: '',
            fromDate: '',
            toDate: '',
            year: currentYear,
        };
        updateEvents([...eventsWithIds, newEvent]);
        // Expand the year when adding an event
        setExpandedYears(new Set([...expandedYears, currentYear]));
    };

    const copyEvent = (eventId: string) => {
        const eventToCopy = eventsWithIds.find(e => e.id === eventId);
        if (!eventToCopy) {
            return;
        }

        const newEvent: SamplingEventWithId = {
            ...eventToCopy,
            id: `event-${nextIdRef.current++}`,
            name: `${eventToCopy.name} (Copy)`,
        };
        updateEvents([...eventsWithIds, newEvent]);
    };

    const removeEvent = (eventId: string) => {
        const indexToRemove = eventsWithIds.findIndex(e => e.id === eventId);
        if (indexToRemove === -1) {
            return;
        }

        // Remove the corresponding ID from the IDs array
        const newIds = [...eventIdsRef.current];
        newIds.splice(indexToRemove, 1);
        eventIdsRef.current = newIds;

        updateEvents(eventsWithIds.filter(e => e.id !== eventId));
    };

    const reorderEvent = (eventId: string, direction: 'up' | 'down') => {
        const event = eventsWithIds.find(e => e.id === eventId);
        if (!event) {
            return;
        }

        // Find the year group this event belongs to
        const yearEvents = eventsByYear[event.year] || [];
        const yearEventIndex = yearEvents.findIndex(e => e.id === eventId);

        if (yearEventIndex === -1) {
            return;
        }

        // Calculate new position within the year
        const newYearIndex = direction === 'up' ? yearEventIndex - 1 : yearEventIndex + 1;
        if (newYearIndex < 0 || newYearIndex >= yearEvents.length) {
            return;
        }

        // Find the target event in the same year
        const targetEvent = yearEvents[newYearIndex];

        // Find global indices for both events
        const currentGlobalIndex = eventsWithIds.findIndex(e => e.id === eventId);
        const targetGlobalIndex = eventsWithIds.findIndex(e => e.id === targetEvent.id);

        if (currentGlobalIndex === -1 || targetGlobalIndex === -1) {
            return;
        }

        // Swap events in the global array
        const newEvents = [...eventsWithIds];
        [newEvents[currentGlobalIndex], newEvents[targetGlobalIndex]] = [newEvents[targetGlobalIndex], newEvents[currentGlobalIndex]];

        // Also reorder the IDs array to maintain consistency
        const newIds = [...eventIdsRef.current];
        [newIds[currentGlobalIndex], newIds[targetGlobalIndex]] = [newIds[targetGlobalIndex], newIds[currentGlobalIndex]];
        eventIdsRef.current = newIds;

        // Remember which event was focused
        setFocusedEventId(eventId);
        updateEvents(newEvents);
    };

    // Restore focus after reordering or year change
    useEffect(() => {
        if (focusedEventId && eventRefs.current[focusedEventId]) {
            const element = eventRefs.current[focusedEventId];
            if (element) {
                // Find the first input in the event card and focus it
                const firstInput = element.querySelector('input') as HTMLInputElement;
                if (firstInput) {
                    setTimeout(() => {
                        firstInput.focus();
                        // Scroll the element into view if needed
                        element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }, 100);
                }
                setFocusedEventId(null);
            }
        }
    }, [samplingEvents, focusedEventId, eventsByYear]);

    const updateEvent = (eventId: string, updates: Partial<SamplingEventWithId>) => {
        const currentEvent = eventsWithIds.find(e => e.id === eventId);
        const updatedEvents = eventsWithIds.map(event => {
            if (event.id === eventId) {
                const updated = { ...event, ...updates };
                // Automatically infer year from fromDate when fromDate changes
                if (updates.fromDate !== undefined) {
                    if (updates.fromDate && updates.fromDate.trim() !== '') {
                        const parsedYear = moment(updates.fromDate).year();
                        const newYear = isNaN(parsedYear) || parsedYear <= 0 ? moment().year() : parsedYear;
                        updated.year = newYear;
                        // If year changed, expand the new year section and retain focus
                        if (currentEvent && currentEvent.year !== newYear) {
                            setExpandedYears(new Set([...expandedYears, newYear]));
                            setFocusedEventId(eventId);
                        }
                    } else {
                        // If fromDate is cleared, keep current year or use current year
                        updated.year = updated.year && updated.year > 0 ? updated.year : moment().year();
                    }
                }
                return updated;
            }
            return event;
        });
        updateEvents(updatedEvents);
    };

    const toggleYear = (year: number) => {
        const newExpanded = new Set(expandedYears);
        if (newExpanded.has(year)) {
            newExpanded.delete(year);
        } else {
            newExpanded.add(year);
        }
        setExpandedYears(newExpanded);
    };

    const expandAll = () => {
        setExpandedYears(new Set(Object.keys(eventsByYear).map(Number)));
    };

    const collapseAll = () => {
        setExpandedYears(new Set());
    };

    const years = Object.keys(eventsByYear)
        .map(Number)
        .sort((a, b) => b - a);
    const hasEvents = eventsWithIds.length > 0;

    return (
        <div className={styles.samplingEventsManager}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={commonStyles.formInputSectionTitle}>
                        Sampling events
                        <QuestionButtonHelp type='api' slug='project-definition-sampling-event' />
                    </div>
                    <div className={styles.groupingHint}>
                        Events are automatically grouped by year based on their &quot;From date&quot;. Years are displayed in descending
                        order (newest first).
                    </div>
                    {hasEvents && (
                        <div className={styles.yearControls}>
                            <button type='button' onClick={expandAll} className={styles.expandButton}>
                                Expand all
                            </button>
                            <button type='button' onClick={collapseAll} className={styles.expandButton}>
                                Collapse all
                            </button>
                        </div>
                    )}
                </div>
                <PrimaryButton onClick={() => addEvent()} className={styles.addButton}>
                    <Plus />
                    Add event
                </PrimaryButton>
            </div>

            {!hasEvents ? (
                <div className={styles.emptyState}>
                    <p>No sampling events added yet. Click &quot;Add event&quot; to get started.</p>
                </div>
            ) : (
                <>
                    <div className={styles.eventsContainer}>
                        {years.map(year => {
                            const yearEvents = eventsByYear[year];
                            const isExpanded = expandedYears.has(year);
                            const yearEventCount = yearEvents.length;

                            return (
                                <div key={year} className={styles.yearGroup}>
                                    <div className={styles.yearHeader} onClick={() => toggleYear(year)}>
                                        <div className={styles.yearHeaderContent}>
                                            {isExpanded ? (
                                                <ChevronUp className={styles.expandIcon} />
                                            ) : (
                                                <ChevronDown className={styles.expandIcon} />
                                            )}
                                            <span className={styles.yearLabel}>Year {year}</span>
                                            <span className={styles.yearEventCount}>
                                                ({yearEventCount} {yearEventCount === 1 ? 'event' : 'events'})
                                            </span>
                                        </div>
                                        <SecondaryButton
                                            onClick={e => {
                                                e.stopPropagation();
                                                addEvent(year);
                                            }}
                                            className={styles.addYearEventButton}
                                        >
                                            <Plus />
                                            Add to {year}
                                        </SecondaryButton>
                                    </div>

                                    {isExpanded && (
                                        <div className={styles.yearEvents}>
                                            {yearEvents.map((event, eventIndex) => {
                                                const globalIndex = eventsWithIds.findIndex(e => e.id === event.id);
                                                const { showErrorBorder, errorMessages } = validateSamplingEvent(formState, globalIndex);

                                                const hasErrors = errorMessages && errorMessages.length > 0;
                                                const isErrorExpanded = expandedErrors.has(event.id);
                                                const isLastEvent = eventIndex === yearEvents.length - 1;

                                                return (
                                                    <React.Fragment key={event.id}>
                                                        <div
                                                            ref={el => {
                                                                eventRefs.current[event.id] = el;
                                                            }}
                                                            className={classNames(styles.eventCard, {
                                                                [styles.eventCardError]: showErrorBorder,
                                                            })}
                                                        >
                                                            <div className={styles.eventHeader}>
                                                                <div className={styles.eventNumber}>Event {eventIndex + 1}</div>
                                                                <div className={styles.eventActions}>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => copyEvent(event.id)}
                                                                        className={styles.actionButton}
                                                                        title='Copy event'
                                                                    >
                                                                        <Copy />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => reorderEvent(event.id, 'up')}
                                                                        disabled={eventIndex === 0}
                                                                        className={styles.actionButton}
                                                                        title='Move up'
                                                                    >
                                                                        <ChevronUp />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => reorderEvent(event.id, 'down')}
                                                                        disabled={eventIndex === yearEvents.length - 1}
                                                                        className={styles.actionButton}
                                                                        title='Move down'
                                                                    >
                                                                        <ChevronDown />
                                                                    </button>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => removeEvent(event.id)}
                                                                        className={classNames(styles.actionButton, styles.deleteButton)}
                                                                        title='Delete event'
                                                                    >
                                                                        <Trash2 />
                                                                    </button>
                                                                </div>
                                                            </div>

                                                            <div className={styles.eventContent}>
                                                                <div className={styles.eventField}>
                                                                    <div className={styles.fieldLabel}>
                                                                        Event name <span className={styles.requiredIndicator}>*</span>
                                                                    </div>
                                                                    <Input
                                                                        value={event.name}
                                                                        onChange={e => updateEvent(event.id, { name: e.target.value })}
                                                                        maxLength={25}
                                                                        placeholder='Enter event name'
                                                                    />
                                                                </div>

                                                                <div className={styles.eventField}>
                                                                    <div className={styles.fieldLabel}>
                                                                        From date <span className={styles.requiredIndicator}>*</span>
                                                                    </div>
                                                                    <BasicDatePicker
                                                                        value={event.fromDate ? new Date(event.fromDate) : null}
                                                                        onChange={value => {
                                                                            const dateString = value?.toDateString();
                                                                            // Year is automatically inferred from fromDate
                                                                            updateEvent(event.id, {
                                                                                fromDate: dateString || '',
                                                                            });
                                                                        }}
                                                                    />
                                                                </div>

                                                                <div className={styles.eventField}>
                                                                    <div className={styles.fieldLabel}>
                                                                        To date <span className={styles.requiredIndicator}>*</span>
                                                                    </div>
                                                                    <BasicDatePicker
                                                                        value={event.toDate ? new Date(event.toDate) : null}
                                                                        onChange={value =>
                                                                            updateEvent(event.id, { toDate: value?.toDateString() || '' })
                                                                        }
                                                                    />
                                                                </div>
                                                            </div>

                                                            {hasErrors && (
                                                                <div className={styles.eventErrorsContainer}>
                                                                    <button
                                                                        type='button'
                                                                        onClick={() => {
                                                                            const newExpanded = new Set(expandedErrors);
                                                                            if (newExpanded.has(event.id)) {
                                                                                newExpanded.delete(event.id);
                                                                            } else {
                                                                                newExpanded.add(event.id);
                                                                            }
                                                                            setExpandedErrors(newExpanded);
                                                                        }}
                                                                        className={styles.errorToggle}
                                                                    >
                                                                        <AlertCircle className={styles.errorIcon} />
                                                                        <span className={styles.errorCount}>
                                                                            {errorMessages.length}{' '}
                                                                            {errorMessages.length === 1 ? 'error' : 'errors'}
                                                                        </span>
                                                                        {isErrorExpanded ? (
                                                                            <ChevronUp className={styles.errorExpandIcon} />
                                                                        ) : (
                                                                            <ChevronDown className={styles.errorExpandIcon} />
                                                                        )}
                                                                    </button>
                                                                    {isErrorExpanded && (
                                                                        <div className={styles.eventErrors}>
                                                                            {errorMessages.map((message, idx) => (
                                                                                <div key={idx} className={styles.errorMessage}>
                                                                                    {message}
                                                                                </div>
                                                                            ))}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {isLastEvent && (
                                                            <div className={styles.addEventAfterLast}>
                                                                <SecondaryButton
                                                                    onClick={() => addEvent(year)}
                                                                    className={styles.addButton}
                                                                >
                                                                    <Plus />
                                                                    Add event to {year}
                                                                </SecondaryButton>
                                                            </div>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
};

export default SamplingEventsManager;
